/**
 * PredictionPanel — Prediction list, batch summaries, model stats
 * Phase 4.8-B — WQ-198 (initial)
 * Phase 4.10 β3 — WQ-238: '+ New Prediction' button + PredictionRequestForm invocation
 */
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { RefreshIcon, SearchIcon, DownloadIcon } from '../common/Icons';
import { listPredictions, getBatchSummary, exportBatchCsv, type Prediction, type BatchSummary } from '../../services/predictionBIService';
import PredictionRequestForm from './PredictionRequestForm';

const STATUS_COLORS: Record<string, string> = {
  completed: '#22c55e', pending: '#eab308', failed: '#dc2626', processing: '#3b82f6',
};

const PredictionPanel: React.FC = () => {
  const { authFetch, isLoading: authLoading } = useAuth();
  const [predictions, setPredictions] = useState<Prediction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [batchSummary, setBatchSummary] = useState<BatchSummary | null>(null);
  const [selectedBatchId, setSelectedBatchId] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const filters: Record<string, string | number> = { limit: 100 };
      if (typeFilter) filters.prediction_type = typeFilter;
      const data = await listPredictions(authFetch, filters);
      setPredictions(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load predictions');
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!authLoading) fetchData();
  }, [authLoading, typeFilter]);

  const handleViewBatch = async (batchId: string) => {
    setSelectedBatchId(batchId);
    try {
      const summary = await getBatchSummary(authFetch, batchId);
      setBatchSummary(summary);
    } catch {
      setBatchSummary(null);
    }
  };

  const handleExportCsv = async (batchId: string) => {
    try {
      const blob = await exportBatchCsv(authFetch, batchId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `predictions_${batchId}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Export failed');
    }
  };

  const filtered = predictions.filter(p =>
    !searchQuery ||
    p.model_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.model_id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.batch_id?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const uniqueTypes = [...new Set(predictions.map(p => p.prediction_type).filter(Boolean))].sort();
  const uniqueBatches = [...new Set(predictions.map(p => p.batch_id).filter(Boolean))] as string[];

  if (loading && predictions.length === 0) {
    return <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-secondary)' }}>Loading predictions...</div>;
  }

  return (
    <div>
      {/* Batch summary panel */}
      {batchSummary && (
        <div style={{
          padding: 14, borderRadius: 8, border: '1px solid var(--border)',
          background: 'var(--bg-secondary)', marginBottom: 16,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <strong style={{ fontSize: 13 }}>Batch: {batchSummary.batch_id}</strong>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => handleExportCsv(batchSummary.batch_id)} style={{
                fontSize: 11, padding: '3px 8px', borderRadius: 4,
                border: '1px solid var(--border)', background: 'var(--bg-primary)',
                color: 'var(--text-primary)', cursor: 'pointer',
                display: 'inline-flex', alignItems: 'center', gap: 4,
              }}>
                <DownloadIcon size={12} /> Export CSV
              </button>
              <button onClick={() => { setBatchSummary(null); setSelectedBatchId(null); }} style={{
                fontSize: 11, padding: '3px 8px', borderRadius: 4,
                border: '1px solid var(--border)', background: 'var(--bg-primary)',
                color: 'var(--text-primary)', cursor: 'pointer',
              }}>Close</button>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            {[
              { label: 'Total', value: batchSummary.total },
              { label: 'Completed', value: batchSummary.completed, color: '#22c55e' },
              { label: 'Failed', value: batchSummary.failed, color: '#dc2626' },
              { label: 'Avg Confidence', value: batchSummary.avg_confidence !== null ? `${(batchSummary.avg_confidence * 100).toFixed(1)}%` : '—' },
              { label: 'Avg Latency', value: batchSummary.avg_latency_ms !== null ? `${batchSummary.avg_latency_ms.toFixed(0)}ms` : '—' },
            ].map(s => (
              <div key={s.label} style={{ fontSize: 12 }}>
                <span style={{ color: 'var(--text-secondary)' }}>{s.label}: </span>
                <strong style={{ color: 'color' in s && s.color ? s.color : 'var(--text-primary)' }}>{s.value}</strong>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, maxWidth: 360 }}>
          <SearchIcon size={16} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)' }} />
          <input
            type="text" placeholder="Search predictions..." value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            style={{ width: '100%', padding: '8px 8px 8px 32px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: 13 }}
          />
        </div>
        <select
          value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
          style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: 13 }}
        >
          <option value="">All Types</option>
          {uniqueTypes.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        {uniqueBatches.length > 0 && (
          <select
            value={selectedBatchId || ''} onChange={e => e.target.value ? handleViewBatch(e.target.value) : setBatchSummary(null)}
            style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: 13 }}
          >
            <option value="">View Batch...</option>
            {uniqueBatches.map(b => <option key={b} value={b}>{b.slice(0, 12)}...</option>)}
          </select>
        )}
        <button onClick={fetchData} style={{
          fontSize: 13, padding: '6px 12px', borderRadius: 6, border: '1px solid var(--border)',
          fontWeight: 500, cursor: 'pointer', background: 'var(--bg-secondary)', color: 'var(--text-primary)',
          display: 'inline-flex', alignItems: 'center', gap: 4,
        }}>
          <RefreshIcon size={14} /> Refresh
        </button>
        <button
          data-testid="prediction-panel-new-prediction"
          onClick={() => setFormOpen(true)}
          style={{
            fontSize: 13, padding: '6px 16px', borderRadius: 6, border: 'none',
            fontWeight: 600, cursor: 'pointer',
            background: 'var(--accent-primary)', color: '#fff',
          }}
        >
          + New Prediction
        </button>
      </div>

      {error && <div style={{ padding: 12, background: '#fef2f2', color: '#dc2626', borderRadius: 8, marginBottom: 16 }}>{error}</div>}

      {filtered.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>No predictions found.</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--border)' }}>
                <th style={{ textAlign: 'left', padding: '8px 12px' }}>Model</th>
                <th style={{ textAlign: 'left', padding: '8px 12px' }}>Type</th>
                <th style={{ textAlign: 'center', padding: '8px 12px' }}>Confidence</th>
                <th style={{ textAlign: 'center', padding: '8px 12px' }}>Latency</th>
                <th style={{ textAlign: 'center', padding: '8px 12px' }}>Status</th>
                <th style={{ textAlign: 'right', padding: '8px 12px' }}>Created</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(pred => (
                <tr key={pred.id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '8px 12px', fontWeight: 600 }}>{pred.model_name || pred.model_id.slice(0, 12)}</td>
                  <td style={{ padding: '8px 12px' }}>{pred.prediction_type}</td>
                  <td style={{ textAlign: 'center', padding: '8px 12px', fontFamily: 'monospace', fontSize: 12 }}>
                    {pred.confidence !== null ? `${(pred.confidence * 100).toFixed(1)}%` : '—'}
                  </td>
                  <td style={{ textAlign: 'center', padding: '8px 12px', fontSize: 12 }}>
                    {pred.latency_ms !== null ? `${pred.latency_ms}ms` : '—'}
                  </td>
                  <td style={{ textAlign: 'center', padding: '8px 12px' }}>
                    <span style={{
                      fontSize: 11, padding: '2px 8px', borderRadius: 10,
                      background: `${STATUS_COLORS[pred.status] || '#888'}20`,
                      color: STATUS_COLORS[pred.status] || '#888',
                    }}>{pred.status}</span>
                  </td>
                  <td style={{ textAlign: 'right', padding: '8px 12px', fontSize: 12, color: 'var(--text-secondary)' }}>
                    {new Date(pred.created_at).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 8 }}>
        Showing {filtered.length} predictions
      </div>

      <PredictionRequestForm
        isOpen={formOpen}
        onClose={() => setFormOpen(false)}
        onSubmitted={() => fetchData()}
      />
    </div>
  );
};

export default PredictionPanel;
